import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import Form, { SIZE, TYPE } from "components/Form";
import { USER_REGISTER } from "graphql/mutations";
import { GET_PROFILE } from "graphql/queries";
import { MUTATION_TYPE, useLinks } from "hooks";
import { validatePassword } from "utils";
import { PrimaryLink } from "components/Links";
import { LINKED_IN_PREFIX, TALENT_SIMPLE_ARCHETYPES_OPTIONS } from "constants/index";
import { margins } from "style";

/**
 * FormEmail
 *
 * @param {Boolean}    isSingleStep
 * @param {Function}   onChange
 * @param {Function}   onCancel
 * @param {Function}   onCompleted
 * @param {Object}     values
 */
const FormEmail = ({
  isSingleStep,
  titleId = "Signup.FormEmail.FormLabelTitle",
  onChange,
  onCancel,
  onCompleted,
  values,
}) => {
  const links = useLinks();
  const [initialValues] = useState(values);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const passwordResult = validatePassword(values?.password);

  useEffect(() => {
    const pw = values?.password;
    const pwConfirm = values?.passwordConfirmation;

    setPasswordsMatch(!pwConfirm || pwConfirm?.length === 0 || pwConfirm === pw);
  }, [values]);

  return (
    <Form
      data={FORM_DATA(
        passwordsMatch,
        !values?.password?.length || passwordResult.isValid,
        links.terms_and_conditions,
        isSingleStep,
        titleId
      )}
      initialValues={initialValues}
      onChange={onChange}
      onCompleted={onCompleted}
      marginSize={margins.none}
      mutationType={MUTATION_TYPE.add}
      mutationData={{
        mutationName: {
          add: USER_REGISTER,
        },
        refetchQueries: [GET_PROFILE],
        refetchAfterMutate: false,
        unWrapParams: true,
      }}
      cancelButton={{
        show: !isSingleStep,
        labelId: "Global.GoBack",
        onClick: onCancel,
      }}
      saveButton={{
        show: true,
        labelId: isSingleStep ? "Signup.FormRoleEmail.SaveButton" : "Global.Complete",
        disabled:
          !values?.firstName ||
          !values?.lastName ||
          !values?.email ||
          !values?.password ||
          !values?.passwordConfirmation ||
          (isSingleStep && !values?.archetype) ||
          !passwordsMatch ||
          !passwordResult.isValid,
      }}
    />
  );
};

const FORM_DATA = (passwordsMatch, isValidPassword, termsAndConditionsLink, isSingleStep, titleId) => [
  {
    style: css`
      width: 100%;
      padding-top: 0;
      padding-bottom: 0;
    `,
    items: [
      {
        label: <FormattedMessage id={titleId} />,
        type: TYPE.h4,
        size: SIZE.xlarge,
      },
      {
        type: TYPE.input,
        size: SIZE.xlarge,
        label: <FormattedMessage id="Signup.FormEmail.FormLabelFirstName" />,
        properties: {
          name: "firstName",
          placeholder: "First name",
          autoFocus: true,
          required: true,
          autoComplete: "given-name",
        },
      },
      {
        type: TYPE.input,
        size: SIZE.xlarge,
        label: <FormattedMessage id="Signup.FormEmail.FormLabelLastName" />,
        properties: {
          name: "lastName",
          placeholder: "Last name",
          required: true,
          autoComplete: "family-name",
        },
      },
      {
        type: TYPE.input,
        size: SIZE.xlarge,
        label: <FormattedMessage id="Signup.FormEmail.FormLabelLinkedIn" />,
        properties: {
          type: "url",
          name: "linkedinUrl",
          placeholder: LINKED_IN_PREFIX,
        },
      },
      isSingleStep && {
        type: TYPE.select,
        size: SIZE.xlarge,
        label: <FormattedMessage id="Signup.FormEmail.FormLabelArchetype" />,
        properties: {
          name: "archetype",
          placeholder: "Choose archetype...",
          required: true,
          options: TALENT_SIMPLE_ARCHETYPES_OPTIONS,
          labelKey: "name",
          valueKey: "id",
        },
      },
      {
        type: TYPE.input,
        size: SIZE.xlarge,
        label: <FormattedMessage id="Signup.FormEmail.FormLabelEmail" />,
        properties: {
          name: "email",
          placeholder: "Enter email...",
          required: true,
          type: "email",
          autoComplete: "email",
        },
      },
      {
        type: TYPE.password,
        size: SIZE.xlarge,
        label: <FormattedMessage id="Signup.FormEmail.FormLabelPassword" />,
        hasError: !isValidPassword,
        errorMessage: <FormattedMessage id="Global.Password.DoesNotMeetRequirements" />,
        properties: {
          name: "password",
          type: "password",
          autoComplete: "new-password",
          required: true,
        },
      },
      {
        type: TYPE.input,
        size: SIZE.xlarge,
        label: <FormattedMessage id="Signup.FormEmail.FormLabelPasswordConfirm" />,
        hasError: !passwordsMatch,
        errorMessage: <FormattedMessage id="Global.Password.DoesNotMatch" />,
        properties: {
          name: "passwordConfirmation",
          type: "password",
          autoComplete: "new-password",
          required: true,
        },
      },
      {
        type: TYPE.checkbox,
        size: SIZE.xlarge,
        properties: {
          name: "privacyPolicy",
          type: "checkbox",
          required: true,
          label: (
            <FormattedMessage
              id="Signup.FormEmail.PrivacyMsg"
              values={{
                link: (
                  <PrimaryLink href="/legal/privacy-policy" target="_blank">
                    <FormattedMessage id="Signup.FormEmail.PrivacyLink" />
                  </PrimaryLink>
                ),
              }}
            />
          ),
        },
      },
      {
        type: TYPE.checkbox,
        size: SIZE.xlarge,
        properties: {
          name: "termsService",
          type: "checkbox",
          required: true,
          label: (
            <FormattedMessage
              id="Signup.FormEmail.TermsAndConditionsMsg"
              values={{
                link: (
                  <PrimaryLink href={termsAndConditionsLink} target="_blank">
                    <FormattedMessage id="Signup.FormEmail.TermsAndConditionsLink" />
                  </PrimaryLink>
                ),
              }}
            />
          ),
        },
      },
    ].filter(Boolean),
  },
];

FormEmail.propTypes = {
  isSingleStep: PropTypes.bool,
  titleId: PropTypes.string,
  onChange: PropTypes.func,
  onCancel: PropTypes.func,
  onCompleted: PropTypes.func,
  values: PropTypes.object,
};

export default FormEmail;
